// src/db/enhancedIndexedDBSync.js
import { statusDetector } from '../utils/statusDetector';
import { statusMonitor } from '../utils/StatusMonitor';
import { sendExternalFeedback } from '../db/externalEndpoints/externalFeedbackService.js';
import { 
  initDb,
  markQuestion1DataAsSynced,
  markQuestion2DataAsSynced,
  markQuestion3DataAsSynced,
  markComplaintDataAsSynced
} from './indexedDb';
import { api } from '../api/index';

// Function to get total unsynced count from IndexedDB
export const getUnsyncedCount = async () => {
  try {
    const db = await initDb();
    if (!db) {
      throw new Error('Database not initialized');
    }

    // Count from all tables
    const counts = await Promise.all([
      countUnsyncedRecords(db, 'question_1_scale'),
      countUnsyncedRecords(db, 'question_2_yesno'),
      countUnsyncedRecords(db, 'question_3_yesno'),
      countUnsyncedRecords(db, 'complaint')
    ]);

    const totalCount = counts.reduce((sum, count) => sum + count, 0);
    return totalCount;
  } catch (error) {
    console.error('Error getting unsynced count:', error);
    statusDetector.activeIssues.add('indexeddb-error');
    return 0;
  }
};

// Helper function to count unsynced records in a specific store
const countUnsyncedRecords = (db, storeName) => {
  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction([storeName], 'readonly');
      const objectStore = transaction.objectStore(storeName);
      const index = objectStore.index('syncedIndex');
      const request = index.count(IDBKeyRange.only(0));

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onerror = (event) => {
        console.error(`Error counting unsynced records in ${storeName}:`, event.target.error);
        resolve(0); // Return 0 on error instead of failing
      };
    } catch (error) {
      console.error(`Error accessing ${storeName}:`, error);
      resolve(0);
    }
  });
};

// Helper function to fetch unsynced records from a specific store
const fetchUnsyncedFromStore = async (storeName) => {
  try {
    const db = await initDb();
    if (!db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readonly');
      const objectStore = transaction.objectStore(storeName);
      const index = objectStore.index('syncedIndex');
      const request = index.getAll(IDBKeyRange.only(0));

      request.onsuccess = (event) => resolve(event.target.result);
      request.onerror = (event) => {
        console.error(`Error fetching unsynced from ${storeName}:`, event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error(`Error fetching unsynced from ${storeName}:`, error);
    return [];
  }
};

// Enhanced sync function with detailed monitoring
export const enhancedSyncDataToCloud = async () => {
  const syncStartTime = Date.now();
  let syncMetadata = {
    syncAttempt: true,
    startTime: new Date().toISOString()
  };

  console.log('🔄 ===== SYNC ATTEMPT STARTED =====');

  // CRITICAL: Check if offline before attempting sync
  if (!navigator.onLine) {
    console.log('❌ Device is offline, skipping sync attempt');
    statusDetector.activeIssues.add('offline');
    statusDetector.handleSyncAttempt(false, {
      ...syncMetadata,
      failed: true,
      errorMessage: 'Device offline',
      skipSync: true
    });
    return { 
      success: false, 
      error: 'Device offline',
      skipped: true
    };
  }

  try {
    console.log('✅ Device is online, proceeding with sync');

    // Pre-sync health check
    await statusDetector.performFullHealthCheck(getUnsyncedCount);

    // Double-check we're still online after health check
    if (!navigator.onLine) {
      console.log('❌ Device went offline during health check');
      statusDetector.activeIssues.add('offline');
      statusDetector.handleSyncAttempt(false, {
        ...syncMetadata,
        failed: true,
        errorMessage: 'Device went offline'
      });
      return { 
        success: false, 
        error: 'Device went offline',
        skipped: true
      };
    }

    // Get unsynced count before sync
    const unsyncedCountBefore = await getUnsyncedCount();
    console.log(`📊 Unsynced records before sync: ${unsyncedCountBefore}`);
    
    if (unsyncedCountBefore === 0) {
      console.log('✅ No data to sync');
      statusDetector.handleSyncAttempt(true, {
        ...syncMetadata,
        syncSuccess: true,
        recordsSynced: 0,
        duration: Date.now() - syncStartTime
      });
      return { success: true, synced: 0 };
    }

    console.log(`🚀 Starting sync of ${unsyncedCountBefore} unsynced records`);

    // Fetch all unsynced data from all stores
    const question1Data = await fetchUnsyncedFromStore('question_1_scale');
    const question2Data = await fetchUnsyncedFromStore('question_2_yesno');
    const question3Data = await fetchUnsyncedFromStore('question_3_yesno');
    const complaintData = await fetchUnsyncedFromStore('complaint');

    console.log('📦 Data fetched:', {
      question1: question1Data.length,
      question2: question2Data.length,
      question3: question3Data.length,
      complaint: complaintData.length
    });

    const data = {
      question1: question1Data,
      question2: question2Data,
      question3: question3Data,
      complaint: complaintData,
    };

    // send external feedback for question 1 data for adelaide oval
        try {
          for (const entry of question1Data) {
            const externalPayload = entry.payload_adelaide_oval;

            if (Object.keys(externalPayload).length !== 0) { // only send if payload is not empty
              const org_id = 1; // adelaide oval org_id is 1
              const externalResponse = await sendExternalFeedback(externalPayload, org_id);
              if (externalResponse.success) {
                console.log('External feedback sent successfully for question 1 entry id:', entry.id);
              } else {
                console.error('Failed to send external feedback for question 1 entry id:', entry.id, 'Error:', externalResponse.error);
              }
            }
          }
        } catch (externalError) {
          console.error('Error sending external feedback to adelaide oval for question 1 data:', externalError);
        }

    // Attempt sync with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

    console.log(`📡 Sending POST request to: ${api}/feedback`);

    const response = await fetch(`${api}/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    console.log(`📨 Response received. Status: ${response.status} ${response.statusText}`);

    // Handle response
    if (!response.ok) {
      console.log('❌ Response not OK. Status:', response.status);
      
      let errorMessage = response.statusText;
      let errorType = 'unknown';
      let errorData = null;
      let errorCode = null;
      
      try {
        errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
        errorType = errorData.errorType || 'unknown';
        errorCode = errorData.errorCode || null;
        
        console.error('📋 Parsed error response:', {
          status: response.status,
          errorType: errorType,
          errorCode: errorCode,
          errorMessage: errorMessage,
          fullErrorData: errorData
        });
      } catch (e) {
        console.error(`❌ Failed to parse error response: ${e.message}`);
      }

      // CRITICAL: Clear previous errors before setting new one
      console.log('🧹 Clearing all error states...');
      statusDetector.clearAllErrorStates();

      console.log('🔍 Checking response status:', response.status);

      // Categorize the error based on response status and errorType from backend
      if (response.status === 503) {
        console.log('✅ Status is 503 - Service Unavailable');
        console.log('🔍 Error type received:', errorType);
        
        if (errorType === 'rds_auth_error') {
          console.error('🔐 Setting status to: rds-auth-error');
          statusDetector.activeIssues.add('rds-auth-error');
          statusDetector.lastErrorType = 'rds-auth-error';
          console.log('✅ Active issues after setting:', Array.from(statusDetector.activeIssues));
        } else if (errorType === 'rds_unreachable') {
          console.error('🔌 Setting status to: rds-unreachable');
          statusDetector.activeIssues.add('rds-unreachable');
          statusDetector.lastErrorType = 'rds-unreachable';
          console.log('✅ Active issues after setting:', Array.from(statusDetector.activeIssues));
        } else if (errorType === 'rds_connection_error' || errorType === 'rds_connection_lost') {
          console.error('⚠️ Setting status to: rds-error');
          statusDetector.activeIssues.add('rds-error');
          statusDetector.lastErrorType = 'rds-error';
          console.log('✅ Active issues after setting:', Array.from(statusDetector.activeIssues));
        } else {
          console.error('⚡ Setting status to: backend-overload');
          statusDetector.activeIssues.add('backend-overload');
          statusDetector.lastErrorType = 'backend-overload';
          console.log('✅ Active issues after setting:', Array.from(statusDetector.activeIssues));
        }
      } else if (response.status === 207) {
        console.log('✅ Status is 207 - Multi-Status (Partial Success)');
        console.warn('⚠️ Partial sync success:', errorData);
        statusDetector.activeIssues.add('partial-sync');
        
        // Mark only successful records as synced
        try {
          await Promise.all([
            markQuestion1DataAsSynced(),
            markQuestion2DataAsSynced(),
            markQuestion3DataAsSynced(),
            markComplaintDataAsSynced()
          ]);
          console.log('✅ Data marked as synced after partial success');
        } catch (markError) {
          console.error('❌ Error marking data as synced after partial sync:', markError);
        }
        
        syncMetadata = {
          ...syncMetadata,
          partialSync: true,
          syncSuccess: true,
          recordsSynced: errorData?.successCount || 0,
          recordsFailed: errorData?.failedCount || 0,
          unsyncedCount: errorData?.failedCount || 0,
          duration: Date.now() - syncStartTime
        };

        statusDetector.handleSyncAttempt(true, syncMetadata);

        return {
          success: true,
          partial: true,
          synced: errorData?.successCount || 0,
          failed: errorData?.failedCount || 0,
          failedRecords: errorData?.failedRecords || []
        };
      } else if (response.status >= 500) {
        console.error('🔴 Status >= 500 - Backend server error');
        statusDetector.activeIssues.add('backend-5xx');
        statusDetector.lastErrorType = 'backend-5xx';
        console.log('✅ Active issues after setting:', Array.from(statusDetector.activeIssues));
      } else if (response.status >= 400) {
        console.error('🟣 Status >= 400 - Client error');
        if (response.status === 401 || response.status === 403) {
          statusDetector.activeIssues.add('auth-error');
          statusDetector.lastErrorType = 'auth-error';
        } else if (response.status === 422) {
          statusDetector.activeIssues.add('data-validation-error');
          statusDetector.lastErrorType = 'data-validation-error';
        } else {
          statusDetector.activeIssues.add('client-4xx');
          statusDetector.lastErrorType = 'client-4xx';
        }
        console.log('✅ Active issues after setting:', Array.from(statusDetector.activeIssues));
      }

      console.log('📊 Final metadata before handleSyncAttempt:', {
        failed: true,
        statusCode: response.status,
        errorMessage: errorMessage,
        errorType: errorType,
        errorCode: errorCode
      });

      syncMetadata = {
        ...syncMetadata,
        failed: true,
        statusCode: response.status,
        errorMessage: errorMessage,
        errorType: errorType,
        errorCode: errorCode,
        unsyncedCount: unsyncedCountBefore
      };

      console.log('🎯 Calling handleSyncAttempt with failure...');
      statusDetector.handleSyncAttempt(false, syncMetadata);
      console.log('📋 Status after handleSyncAttempt:', statusDetector.lastErrorType);
      console.log('📋 Current statusMonitor status:', statusMonitor.getStatus());

      return { 
        success: false, 
        error: `HTTP ${response.status}: ${errorMessage}`,
        errorType: errorType,
        errorCode: errorCode,
        unsyncedCount: unsyncedCountBefore
      };
    }

    // Sync successful - mark data as synced
    console.log('✅ Response OK - marking data as synced');
    try {
      await Promise.all([
        markQuestion1DataAsSynced(),
        markQuestion2DataAsSynced(),
        markQuestion3DataAsSynced(),
        markComplaintDataAsSynced()
      ]);

      console.log('✅ All data marked as synced');
    } catch (markError) {
      console.error('❌ Error marking data as synced:', markError);
      // This is a critical error - data was sent but not marked
      statusDetector.activeIssues.add('indexeddb-error');
      
      syncMetadata = {
        ...syncMetadata,
        failed: true,
        errorMessage: 'Data sent but failed to mark as synced',
        criticalError: true
      };

      statusDetector.handleSyncAttempt(false, syncMetadata);

      return {
        success: false,
        error: 'Failed to mark data as synced',
        synced: unsyncedCountBefore
      };
    }

    // Verify sync success by checking remaining unsynced count
    const unsyncedCountAfter = await getUnsyncedCount();
    const syncedCount = unsyncedCountBefore - unsyncedCountAfter;

    console.log(`📊 Sync verification - Before: ${unsyncedCountBefore}, After: ${unsyncedCountAfter}, Synced: ${syncedCount}`);

    if (unsyncedCountAfter === 0) {
      // Full sync success
      console.log(`✅ Successfully synced ${syncedCount} records`);
      
      syncMetadata = {
        ...syncMetadata,
        syncSuccess: true,
        recordsSynced: syncedCount,
        unsyncedCount: 0,
        duration: Date.now() - syncStartTime
      };

      statusDetector.handleSyncAttempt(true, syncMetadata);

      console.log('🎉 ===== SYNC COMPLETED SUCCESSFULLY =====');

      return { 
        success: true, 
        synced: syncedCount 
      };
      
    } else if (syncedCount > 0) {
      // Partial sync - some records remain
      console.warn(`⚠️ Partial sync: ${syncedCount} synced, ${unsyncedCountAfter} remaining`);
      
      statusDetector.activeIssues.add('partial-sync');
      
      syncMetadata = {
        ...syncMetadata,
        partialSync: true,
        syncSuccess: true,
        recordsSynced: syncedCount,
        recordsFailed: unsyncedCountAfter,
        unsyncedCount: unsyncedCountAfter,
        duration: Date.now() - syncStartTime
      };

      statusDetector.handleSyncAttempt(true, syncMetadata);

      return {
        success: true,
        partial: true,
        synced: syncedCount,
        remaining: unsyncedCountAfter
      };
      
    } else {
      // No records synced - something went wrong
      console.error('❌ Sync completed but no records were marked as synced');
      
      statusDetector.activeIssues.add('indexeddb-error');
      
      syncMetadata = {
        ...syncMetadata,
        failed: true,
        errorMessage: 'No records synced',
        unsyncedCount: unsyncedCountBefore
      };

      statusDetector.handleSyncAttempt(false, syncMetadata);

      return {
        success: false,
        error: 'No records were synced'
      };
    }

  } catch (error) {
    console.error('💥 ===== SYNC FAILED WITH EXCEPTION =====');
    console.error('💥 Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });

    // CRITICAL: Clear previous errors before categorizing new one
    console.log('🧹 Clearing all error states...');
    statusDetector.clearAllErrorStates();

    // Determine error type
    if (error.name === 'AbortError') {
      console.error('⏱️ Sync timeout after 30 seconds');
      statusDetector.activeIssues.add('backend-timeout');
      statusDetector.lastErrorType = 'backend-timeout';
    } else if (!navigator.onLine) {
      console.error('📡 Sync failed: Device offline');
      statusDetector.activeIssues.add('offline');
      statusDetector.lastErrorType = 'offline';
    
    } else if (error.message?.includes('NetworkError') || error.message?.includes('Network request failed')) {
      console.error('🌐 Network error - backend unreachable');
      statusDetector.activeIssues.add('backend-unreachable');
      statusDetector.lastErrorType = 'backend-unreachable';
      console.log('✅ Active issues after setting:', Array.from(statusDetector.activeIssues));
    } else if (error.message?.includes('IndexedDB') || error.message?.includes('database')) {
      console.error('💾 Sync failed: IndexedDB error');
      statusDetector.activeIssues.add('indexeddb-error');
      statusDetector.lastErrorType = 'indexeddb-error';
    } else {
      console.error('❓ Unknown error, categorizing via categorizeApiError');
      statusDetector.categorizeApiError(error, error.response);
    }
    
    syncMetadata = {
      ...syncMetadata,
      failed: true,
      errorName: error.name,
      errorMessage: error.message,
      duration: Date.now() - syncStartTime
    };

    const unsyncedCount = await getUnsyncedCount().catch(() => 0);
    syncMetadata.unsyncedCount = unsyncedCount;

    console.log('🎯 Calling handleSyncAttempt with failure...');
    statusDetector.handleSyncAttempt(false, syncMetadata);
    console.log('📋 Status after handleSyncAttempt:', statusDetector.lastErrorType);
    console.log('📋 Current statusMonitor status:', statusMonitor.getStatus());

    return {
      success: false,
      error: error.message
    };
  }
};
